import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Brand } from "@/components/Brand";
import { useApp } from "@/context/AppContext";
import { apiError, http, HOME, PHONE_PATH, needsPhoneCheck } from "@/lib/api";
import GoogleSignIn from "@/components/GoogleSignIn";

const HERO = "https://images.unsplash.com/photo-1765766601532-90e9b96320c8?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

// entry="start" is onboarding (new clinic / staff code); entry="login" is sign-in for existing users.
export default function Auth({ entry = "login" }) {
  const { login, register, googleAuth } = useApp();
  const nav = useNavigate();
  const { state } = useLocation();
  // The marketing page can deep-link to a specific starting screen (e.g. "clinic" or "join"); default is unchanged.
  const [mode, setMode] = useState(state?.mode || (entry === "start" ? "choice" : "login")); // choice | login | clinic | join | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "", staff_code: "", consent: false });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await http.post("/auth/forgot-password", { email: form.email });
        setNotice(data.message);
      } else if (mode === "login") {
        const u = await login(form.email, form.password);
        nav(needsPhoneCheck(u) ? PHONE_PATH : u.clinic_id || u.role !== "owner" ? HOME : "/setup");
      } else {
        if (!form.consent) throw new Error("Please accept the data consent notice to continue");
        const u = await register({ ...form, staff_code: mode === "join" ? form.staff_code : null });
        nav(needsPhoneCheck(u) ? PHONE_PATH : u.role === "owner" ? "/setup" : HOME);
      }
    } catch (err) {
      setError(err.response ? apiError(err) : err.message);
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = async (credential) => {
    setError("");
    setBusy(true);
    try {
      const u = await googleAuth({ credential, mode: mode === "login" ? "login" : "signup", consent: form.consent, staff_code: mode === "join" ? form.staff_code : null });
      nav(needsPhoneCheck(u) ? PHONE_PATH : u.role === "owner" && !u.clinic_id ? "/setup" : HOME);
    } catch (err) {
      const noAccount = mode === "login" && err.response?.status === 404;
      setError(noAccount ? "No DentMemo account uses this Google email yet. Choose “Create an account” below to set one up." : err.response ? apiError(err) : err.message);
    } finally {
      setBusy(false);
    }
  };
  // Signing up needs the consent box (and a staff code when joining) before Google is offered.
  const googleReady = mode === "login" || (form.consent && (mode !== "join" || form.staff_code.trim().length > 0));
  const googleHint = mode === "join" && !form.staff_code.trim() ? "Enter your staff code and tick the consent box to continue with Google." : "Tick the consent box above to continue with Google.";

  return (
    <div className="grid min-h-screen min-h-dvh lg:grid-cols-[1.05fr_1fr]">
      <div className="relative hidden flex-col justify-between overflow-hidden border-r border-slate-200 p-12 lg:flex">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg,rgba(248,250,252,.4),rgba(248,250,252,.92))" }} />
        <div className="relative"><Brand size="lg" tagline /></div>
        <div className="relative">
          <h1 className="font-head text-5xl font-light leading-[1.05] tracking-tight text-slate-900">
            Every visit, every rupee<br /><span className="font-extrabold">one quiet record.</span>
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-600">
            Patient directory, visit logging, consultant settlements and clinic overview — synced across
            your devices, and working even when the internet drops mid-visit.
          </p>
          <div className="mt-9 grid grid-cols-3 gap-6 border-t border-slate-200 pt-7">
            {[["100%", "Works offline"], ["2", "Doctor / Assistant"], ["₹", "Consultant settlements"]].map(([n, l]) => (
              <div key={l}>
                <p className="font-mono text-2xl font-semibold text-slate-800">{n}</p>
                <p className="mt-1 text-xs text-slate-500">{l}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-slate-500">Encrypted in transit · Encrypted at rest · DPDP-ready consent</p>
      </div>

      <div className="flex items-center px-6 py-14 md:px-14">
        <div className="w-full max-w-md">
          <div className="mb-9 lg:hidden"><Brand tagline /></div>

          {mode === "choice" ? (
            <div className="fade-up" data-testid="auth-choice">
              <h2 className="font-head text-3xl font-light tracking-tight text-slate-900">
                Welcome to <span className="font-extrabold">DentMemo</span>
              </h2>
              <p className="mt-2 text-sm text-slate-600">Let's get your clinic set up.</p>
              <div className="mt-8 space-y-3">
                <button data-testid="choice-new-clinic" onClick={() => setMode("clinic")} className="dm-btn-primary w-full !py-3">Set up a new clinic <ArrowRight size={16} /></button>
                <button data-testid="choice-staff-code" onClick={() => setMode("join")} className="dm-btn-ghost w-full !py-3">I have a staff code</button>
              </div>
              <button type="button" data-testid="choice-login" onClick={() => nav("/login")}
                className="mt-6 w-full text-center text-sm text-slate-500 hover:text-slate-700">
                Already have an account? <span className="font-semibold dm-accent-text">Log in</span>
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="fade-up" data-testid="auth-form">
              <p className="label-xs">{mode === "login" ? "Sign in" : mode === "forgot" ? "Forgot password" : mode === "clinic" ? "Set up a new clinic" : "Join your clinic"}</p>
              <h2 className="mt-2 font-head text-3xl font-light tracking-tight text-slate-900">
                {mode === "login" ? <>Welcome to <span className="font-extrabold">DentMemo</span></> :
                  mode === "forgot" ? <>Reset your <span className="font-extrabold">password</span></> :
                  mode === "clinic" ? <>Create your <span className="font-extrabold">account</span></> :
                  <>Join your <span className="font-extrabold">clinic</span></>}
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                {mode === "login" ? "Let's get your clinic set up." :
                  mode === "forgot" ? "Enter your email and we'll send you a link to set a new password. The link works once and expires in an hour." :
                  mode === "join" ? "Enter the staff code your clinic gave you" : "Tell us about your clinic in the next step."}
              </p>

              <div className="mt-7 space-y-4">
                {mode === "join" && (
                  <div>
                    <p className="label-xs mb-1.5">Staff code</p>
                    <input data-testid="staffcode-input" className="dm-input font-mono tracking-[0.4em]" maxLength={8}
                      value={form.staff_code} onChange={set("staff_code")} placeholder="0000" required />
                  </div>
                )}
                {mode !== "login" && mode !== "forgot" && (
                  <div>
                    <p className="label-xs mb-1.5">{mode === "join" ? "Your name (so your clinic knows it's you)" : "Doctor name"}</p>
                    <input data-testid="name-input" className="dm-input" value={form.name} onChange={set("name")}
                      placeholder={mode === "join" ? "e.g. Priya, front desk" : "Dr. Jane Doe"} required />
                  </div>
                )}
                <div>
                  <p className="label-xs mb-1.5">Email</p>
                  <input type="email" data-testid="email-input" className="dm-input" value={form.email} onChange={set("email")} required />
                </div>
                {mode !== "forgot" && (
                  <div>
                    <p className="label-xs mb-1.5">Password</p>
                    <input type="password" data-testid="password-input" className="dm-input" value={form.password} onChange={set("password")} required />
                  </div>
                )}
                {mode !== "login" && mode !== "forgot" && (
                  <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white/70 p-4 text-sm text-slate-600">
                    <input type="checkbox" data-testid="consent-checkbox" checked={form.consent}
                      onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[color:var(--dm-accent)]" />
                    I consent to DentMemo storing clinic and patient records for treatment record-keeping (DPDP Act, 2023).
                    Data can be exported or deleted anytime from Settings.{" "}
                    By signing up you agree to the{" "}
                    <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold text-slate-800 underline underline-offset-2">Terms</a> and{" "}
                    <a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-slate-800 underline underline-offset-2">Privacy policy</a>.
                  </label>
                )}
              </div>

              {error && <p data-testid="auth-error" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
              {notice && <p data-testid="auth-notice" className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">{notice}</p>}

              <button type="submit" disabled={busy || (mode !== "login" && mode !== "forgot" && !form.consent)} data-testid="auth-submit" className="dm-btn-primary mt-7 w-full !py-3 disabled:opacity-60">
                {busy ? "Loading…" : mode === "forgot" ? "Send reset link" : mode === "join" ? "Join your clinic" : "Continue"} <ArrowRight size={16} />
              </button>
              {(mode === "login" || mode === "clinic" || mode === "join") && (
                <GoogleSignIn mode={mode === "login" ? "login" : "signup"} ready={googleReady} hint={googleHint} onCredential={onGoogle} disabled={busy} />
              )}
              {mode === "login" && (
                <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
                  className="dm-btn-quiet mt-2 w-full">Forgot password?</button>
              )}
              <button type="button" data-testid="auth-back" onClick={() => {
                setError(""); setNotice("");
                if (mode === "forgot") setMode("login");
                else if (mode === "login") nav("/");
                else setMode("choice");
              }} className="dm-btn-quiet mt-2 w-full">Back</button>
              {(mode === "clinic" || mode === "join") && (
                <button type="button" data-testid="auth-switch-to-login" onClick={() => nav("/login")}
                  className="mt-4 w-full text-center text-sm text-slate-500 hover:text-slate-700">
                  Already have an account? <span className="font-semibold dm-accent-text">Log in</span>
                </button>
              )}
              {mode === "login" && (
                <button type="button" data-testid="auth-switch-to-start" onClick={() => nav("/start")}
                  className="mt-4 w-full text-center text-sm text-slate-500 hover:text-slate-700">
                  New to DentMemo? <span className="font-semibold dm-accent-text">Create an account</span>
                </button>
              )}
              {mode === "login" && (
                <p className="mt-5 text-xs text-slate-500">
                  Demo owner <span className="font-mono text-slate-500">doctor@dentmemo.in</span> / <span className="font-mono text-slate-500">Dentmemo@123</span>
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
